import { parseAccessToken, isTokenValid, getCookie } from "@/helpers";
import { isAllowedByRbac } from "@/router/rbac";
import { refreshAccessToken } from "@/api";

/** Public, unauthenticated routes — never guarded, never silently refreshed. */
export const authRoutes = [
  "/login",
  "/register",
  "/forgot-password",
  "/restore-password",
];

/** Minimal shape of the navigation target the guard needs. */
export type NavTarget = { path: string; fullPath: string };

/** What the guard tells vue-router: proceed (true) or redirect. */
export type NavResult = true | { path: string; query?: Record<string, string> };

/**
 * Pure navigation decision for the router's `beforeEach` guard, extracted from
 * `index.ts` so it can be unit-tested without a real router. The route-change
 * request-abort is a side effect and stays in `index.ts`.
 *
 * Rules:
 * - access token missing/expired but a refresh token is still alive → attempt a
 *   silent refresh (skip on auth routes and the public landing `/`, so a user
 *   switching accounts on /login isn't trapped and we don't round-trip there);
 * - authenticated user on an auth route → bounce to /dashboard;
 * - unauthenticated user on a protected route → /login with a redirect query;
 * - authenticated but RBAC-denied → bounce to /dashboard.
 */
export async function resolveNavigation(to: NavTarget): Promise<NavResult> {
  const isAuthRoute = authRoutes.includes(to.path);

  let token = parseAccessToken();

  if (
    !isTokenValid(token) &&
    !isAuthRoute &&
    to.path !== "/" &&
    getCookie("refresh_token")
  ) {
    try {
      await refreshAccessToken();
      token = parseAccessToken();
    } catch {
      // issueNewToken already cleared the cookies on auth failure.
      token = null;
    }
  }

  const isAuthed = isTokenValid(token);

  if (isAuthed && isAuthRoute) {
    return { path: "/dashboard" };
  }

  if (!isAuthed && !isAuthRoute && to.path !== "/") {
    return { path: "/login", query: { redirect: to.fullPath } };
  }

  if (isAuthed && !isAllowedByRbac({ path: to.path, token })) {
    return { path: "/dashboard" };
  }

  return true;
}
